import * as React from "react";
import { useRef, useState } from "react";
import QuestionHeading from "./QuestionHeading";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import QuestionHelpTextModal from "../modals/QuestionHelpTextModal";
import SurveyTranslation from "./SurveyTranslation";
import makeStyles from "@mui/styles/makeStyles";
import { Theme, useMediaQuery, useTheme } from "@mui/material";
import { TApiQuestionEntity } from "../../types/Survey";
import { SURVEY_QUESTION_MAX_CHARACTERS_SYSTEM } from "../../config/surveyQuestionLimits";

interface IQuestionOpenEndedProps {
  question: TApiQuestionEntity;
  value: string;
  setValue: (value: string) => void;
  questionRequired: string | null;
  questionSkipButton: string | null;
  questionContinueButton: string | null;
  handleOpenEndedSubmit: () => void;
  redirectToNextQuestion: (storeQuestion: boolean) => void;
}

const SUBMIT_THROTTLE_MS = 500;

const useStyles = makeStyles((theme: Theme) => ({
  chip: {
    backgroundColor: "orange",
  },
  fieldWrapper: {
    position: "relative",
    "& .MuiOutlinedInput-root": {
      paddingBottom: theme.spacing(3.5),
    },
  },
  counter: {
    position: "absolute",
    right: theme.spacing(2),
    bottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    pointerEvents: "none",
  },
}));

const QuestionOpenEnded: React.FC<IQuestionOpenEndedProps> = ({
  question,
  value,
  setValue,
  questionRequired,
  questionSkipButton,
  questionContinueButton,
  handleOpenEndedSubmit,
  redirectToNextQuestion,
}): JSX.Element => {
  const [modalOpen, setModalOpen] = useState(false);
  const lastSubmittedAt = useRef(0);
  const classes = useStyles({});
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("md"));

  const min = question.minCharacters;
  const max = question.maxCharacters ?? SURVEY_QUESTION_MAX_CHARACTERS_SYSTEM;
  const length = value.length;
  const hasContent = value.trim().length > 0;
  const meetsMinimum = hasContent && (min === null || length >= min);

  const onContinueClick = () => {
    const now = Date.now();
    if (now - lastSubmittedAt.current < SUBMIT_THROTTLE_MS) {
      return;
    }
    lastSubmittedAt.current = now;
    handleOpenEndedSubmit();
  };

  const counterText =
    min !== null && length < min ? `${length}/${min}` : `${length}/${max}`;

  return (
    <>
      <QuestionHelpTextModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        question={question}
      />
      <QuestionHeading question={question} setModalOpen={setModalOpen} />

      <Box className={classes.fieldWrapper}>
        <TextField
          fullWidth
          multiline
          rows={8}
          variant="outlined"
          value={value}
          placeholder={question.answers[0]?.title ?? ""}
          inputProps={{ maxLength: max }}
          onChange={(e) => setValue(e.target.value)}
        />
        <Box className={classes.counter}>
          {min !== null && length < min ? (
            <>
              <SurveyTranslation
                translate={"survey.question.open_ended.minimum"}
              />{" "}
              {counterText}
            </>
          ) : (
            counterText
          )}
        </Box>
      </Box>

      <Grid
        container
        spacing={1}
        justifyContent="flex-end"
        style={{ marginTop: 16 }}
      >
        <Grid
          item
          xs={isXs ? 6 : 3}
          style={{ alignSelf: "center", textAlign: "right" }}
        >
          {question.mandatory ? (
            <Chip
              label={
                questionRequired !== null ? (
                  questionRequired
                ) : (
                  <SurveyTranslation
                    translate={"survey.question.required.label"}
                  />
                )
              }
              classes={{ root: classes.chip }}
            />
          ) : (
            <Button
              fullWidth
              color="primary"
              onClick={() => redirectToNextQuestion(true)}
            >
              {questionSkipButton !== null ? (
                questionSkipButton
              ) : (
                <SurveyTranslation translate={"survey.question.skip"} />
              )}
            </Button>
          )}
        </Grid>
        <Grid item xs={isXs ? 6 : 3}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={!meetsMinimum}
            onClick={onContinueClick}
          >
            {questionContinueButton !== null ? (
              questionContinueButton
            ) : (
              <SurveyTranslation
                translate={"survey.category.continue.button"}
              />
            )}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default QuestionOpenEnded;
